"use client";

import { useState, useCallback, useMemo, useEffect } from "react";
import { useCheckoutBase, defaultGuest } from "./use-checkout-base";
import { participantsToPayload } from "../participants-step";
import { filledSlotCount } from "../utils/participant-seed";
import type { CheckoutStep, Guest } from "../utils/types";

export type UseCheckoutBundleProps = {
  bundle: any;
  course: any;
  user?: any;
  isSendingMaterial?: boolean;
  onValidateDiscount?: (code: string, email: string) => Promise<any>;
  onStepChange?: () => void;
  participantsEnabled?: boolean;
  hasParticipantFields?: boolean;
};

/**
 * Hook for bundle checkout flow (bundlev2)
 * Flow: (Attendees) -> Basket choice
 * No date or ticket selection - bundle is pre-selected
 */
export function useCheckoutBundle({
  bundle,
  course,
  user,
  isSendingMaterial = false,
  onValidateDiscount,
  onStepChange,
  participantsEnabled = false,
  hasParticipantFields = false,
}: UseCheckoutBundleProps) {
  // Base checkout functionality
  const base = useCheckoutBase({
    user,
    enabledAddOns: [], // No add-ons for bundle checkout
    isSendingMaterial,
    onStepChange,
  });

  // Same predicate every flow uses — step shows only when the school enables
  // attendees AND field definitions actually loaded.
  const showParticipantsStep = participantsEnabled && hasParticipantFields;

  // Pre-fill from bundle buyer info
  useEffect(() => {
    if (bundle) {
      const nameParts = bundle.buyerName?.split(" ") || [];
      const bundleGuest: Guest = {
        ...defaultGuest,
        firstName: nameParts[0] || user?.profile?.firstName || "",
        lastName: nameParts.slice(1).join(" ") || user?.profile?.lastName || "",
        email: bundle.buyerEmail || user?.emails?.[0]?.address || "",
        phoneNumber: bundle.buyerPhone || user?.profile?.phone || "",
      };
      base.setPersonalDetails((prev) => ({
        ...prev,
        guests: [bundleGuest],
      }));
    }
  }, [bundle]);

  // The bundle flow has no date/ticket selection and the buyer fills in their
  // details at basket checkout, so there is no in-flow personal step.
  // Participants (attendee) info is NOT collected downstream — it rides the
  // basket emit — so land there first if the step is showing; otherwise
  // basketChoice.
  const [step, setStep] = useState<CheckoutStep>(() =>
    showParticipantsStep ? "participants" : "basketChoice",
  );

  // Calculate total from bundle price
  const total = useMemo(() => {
    const bundlePrice = bundle?.price || 0;

    // Calculate discount based on type
    let discountAmount = 0;
    if (base.discount) {
      const { value, type } = base.discount;
      switch (type) {
        case "PERCENTAGE":
          discountAmount = Math.round((bundlePrice * Math.min(value, 100)) / 100);
          break;
        case "PRICE":
        default:
          discountAmount = Math.min(bundlePrice, value);
          break;
      }
    }

    return Math.max(0, bundlePrice - discountAmount);
  }, [bundle?.price, base.discount]);

  // Calculate the discount amount for display purposes
  const discountAmount = useMemo(() => {
    if (!base.discount) return 0;

    const bundlePrice = bundle?.price || 0;
    const { value, type } = base.discount;

    switch (type) {
      case "PERCENTAGE":
        return Math.round((bundlePrice * Math.min(value, 100)) / 100);
      case "PRICE":
      default:
        return Math.min(bundlePrice, value);
    }
  }, [bundle?.price, base.discount]);

  // Check if booking is free
  const isFree = total === 0;

  // Visible steps for bundle: (participants) -> basketChoice. Buyer info is
  // collected downstream in the basket checkout (bundle purchases are always
  // 1 seat), so there is no personal step.
  const visibleSteps = useMemo((): CheckoutStep[] => {
    const steps: CheckoutStep[] = [];
    // "Who is attending?" — a bundle belongs to one attendee, collected at
    // purchase so redemptions pre-fill. Before basketChoice.
    if (showParticipantsStep) {
      steps.push("participants");
    }
    steps.push("basketChoice");
    return steps;
  }, [showParticipantsStep]);

  // Step navigation
  const currentStepIndex = visibleSteps.indexOf(step);
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === visibleSteps.length - 1;

  const goToNextStep = useCallback(() => {
    const nextIndex = currentStepIndex + 1;
    if (nextIndex < visibleSteps.length) {
      const nextStep = visibleSteps[nextIndex]!;
      setStep(nextStep);
      onStepChange?.();
    }
  }, [currentStepIndex, visibleSteps, onStepChange]);

  const goToPreviousStep = useCallback(() => {
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      const prevStep = visibleSteps[prevIndex]!;
      setStep(prevStep);
      onStepChange?.();
    }
  }, [currentStepIndex, visibleSteps, onStepChange]);

  const goToStep = useCallback(
    (targetStep: CheckoutStep) => {
      if (visibleSteps.includes(targetStep)) {
        setStep(targetStep);
        onStepChange?.();
      }
    },
    [visibleSteps, onStepChange],
  );

  // Apply discount
  const applyDiscount = useCallback(
    async (code: string) => {
      if (!onValidateDiscount) return;

      const email = base.personalDetails.guests[0]?.email || "";
      base.setIsLoading(true);
      base.clearError();

      try {
        const result = await onValidateDiscount(code, email);
        // API returns discount object directly: { value, type, code, percentage, ... }
        // or null/undefined if invalid
        if (result && result.value !== undefined) {
          base.setDiscount({
            value: result.value,
            type: result.type,
            percentage: result.percentage,
          });
          base.setDiscountCode(result.code || code);
        } else {
          const errorMsg = result?.message || "Invalid discount code";
          base.setError(errorMsg);
          throw new Error(errorMsg);
        }
      } catch (error: any) {
        // Extract error message from axios response or error object
        const errorMsg =
          error?.response?.data?.message ||
          (error instanceof Error ? error.message : null) ||
          "Failed to validate discount";
        base.setError(errorMsg);
        // Re-throw with the proper message so the caller can display it
        throw new Error(errorMsg);
      } finally {
        base.setIsLoading(false);
      }
    },
    [onValidateDiscount, base],
  );

  // Clear discount
  const clearDiscount = useCallback(() => {
    base.setDiscount(null);
    base.setDiscountCode("");
  }, [base]);

  // Validate current step
  const canContinue = useCallback((): boolean => {
    switch (step) {
      case "participants":
        // Attendee entry is optional for bundles (gift purchases, undecided
        // attendee) — a fully-empty step may be skipped; anything filled in
        // must be valid. The buyer's auto-seeded "I'm attending" seat isn't
        // something the shopper typed, so it doesn't count as "filled" here.
        // A credit pool has no dated seat to name, so the escape hatch stands
        // even now that the buyer's seat carries a form of its own.
        return (
          base.isParticipantsValid ||
          filledSlotCount(
            base.participantsValue.filter((p) => !p.isSelf),
          ) === 0
        );
      case "basketChoice":
        return true;
      default:
        return false;
    }
  }, [step, base.isParticipantsValid, base.participantsValue]);

  // Get booking data for bundlev2 API
  const getBookingData = useCallback(() => {
    const { personalDetails, userFormValues } = base;
    const [guest, ...additionalGuests] = personalDetails.guests;

    return {
      bookingType: "bundlev2",
      bundleSelected: bundle,
      bundleId: bundle?._id || bundle?.id,
      tickets: [
        {
          ...bundle,
          count: 1,
        },
      ],
      user: {
        _id: user?._id || user?.id,
        profile: {
          firstName: guest?.firstName,
          lastName: guest?.lastName,
          phone: guest?.phoneNumber,
        },
        renderEmail: guest?.email,
      },
      marketingOptIn: guest?.marketingOptIn,
      additionalGuests,
      sessionsIds: [], // No session selection for bundle purchase
      deliveryAddress: isSendingMaterial
        ? personalDetails.deliveryAddress
        : null,
      discountCode: base.discountCode,
      credit: 0,
      timezone: course.timezone,
      userInputs: userFormValues,
      participants: showParticipantsStep
        ? participantsToPayload(base.participantsValue)
        : undefined,
      _pricingSummary: {
        grossTotal: total + discountAmount,
        netTotal: total,
        discountAmount,
        discountType: base.discount ? "discount" : undefined,
        currency: course.currency || "GBP",
      },
    };
  }, [
    base,
    bundle,
    user,
    course.timezone,
    isSendingMaterial,
    total,
    discountAmount,
    showParticipantsStep,
  ]);

  return {
    // State
    step,
    personalDetails: base.personalDetails,
    discount: base.discount,
    discountCode: base.discountCode,
    error: base.error,
    isLoading: base.isLoading,
    isConfirmingFreeBooking: base.isConfirmingFreeBooking,

    // Participants
    showParticipantsStep,
    participantsValue: base.participantsValue,
    setParticipantsValue: base.setParticipantsValue,
    setIsParticipantsValid: base.setIsParticipantsValid,
    buyerAttendeeSeededRef: base.buyerAttendeeSeededRef,

    // Computed
    total,
    discountAmount,
    isFree,
    visibleSteps,
    currentStepIndex,
    isFirstStep,
    isLastStep,
    bundle,
    course,

    // Actions
    goToNextStep,
    goToPreviousStep,
    goToStep,
    updatePersonalDetails: base.updatePersonalDetails,
    updateDeliveryAddress: base.updateDeliveryAddress,
    applyDiscount,
    clearDiscount,
    canContinue,
    getBookingData,
    setIsConfirmingFreeBooking: base.setIsConfirmingFreeBooking,
    clearError: base.clearError,
  };
}
